/** ч.1093: «Вхідні документи» — розпізнавання первинки постачальника (Oblik+/AI). */
import { api } from './client';

export type IncomingStatus = 'QUEUED' | 'RECOGNIZING' | 'RECOGNIZED' | 'CONFIRMED' | 'REJECTED' | 'FAILED' | 'SPLIT';
export type DocKind = 'INVOICE' | 'DELIVERY_NOTE' | 'ACT' | 'OTHER';
export type Target = 'GOODS_RECEIPT' | 'SUPPLIER_INVOICE';

export interface IncomingDocumentListItem {
  id: string;
  organization_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  pages: number | null;
  status: IncomingStatus;
  doc_kind: DocKind | null;
  confidence: string | null;
  error: string | null;
  supplier_name: string | null;
  doc_number: string | null;
  doc_date: string | null;
  total: string | null;
  checks_count: number;
  charged_credits: string | null;
  created_document_type: string | null;
  created_document_id: string | null;
  uploaded_by_name: string | null;
  /** ч.1096: документ, витягнутий з файлу з кількома документами. */
  parent_id: string | null;
  page_from: number | null;
  page_to: number | null;
  created_at: string;
  finished_at: string | null;
}

export interface ChildRef {
  id: string;
  filename: string;
  status: IncomingStatus;
  doc_kind: DocKind | null;
  doc_number: string | null;
  supplier_name: string | null;
}

export interface ResolvedCandidate {
  id: string;
  name: string;
  article?: string | null;
  edrpou?: string | null;
  inn?: string | null;
  is_service?: boolean | null;
}

export interface ResolvedLine {
  line_no: number;
  name: string;
  article: string | null;
  unit: string | null;
  quantity: string;
  price: string;
  vat_rate_id: string | null;
  vat_rate_percent: string;
  amount_without_vat: string;
  vat_amount: string;
  total_amount: string;
  is_service: boolean;
  nomenclature_id: string | null;
  nomenclature_name: string | null;
  /** ч.1096: базова одиниця картки і чи відрізняється від одиниці в документі. */
  card_unit: string | null;
  unit_mismatch: boolean;
  match: 'dictionary' | 'article' | 'name' | 'similar' | null;
  candidates: ResolvedCandidate[];
  uom_id: string | null;
}

export interface ResolvedCheck {
  code: string;
  level: 'warn' | 'error' | 'info';
  message: string;
}

export interface TaxInvoiceCandidate {
  id: string;
  number: string;
  date: string;
  total: string;
  registered: boolean;
  linked: boolean;
  linked_to: string | null;
}

export interface ResolvedTaxInvoice {
  status: 'found' | 'linked_elsewhere' | 'ambiguous' | 'not_found' | 'not_applicable';
  reason: string | null;
  candidates: TaxInvoiceCandidate[];
}

export interface Resolved {
  target: Target;
  doc_kind: DocKind;
  number: string | null;
  date: string | null;
  payment_due_date: string | null;
  prices_include_vat: boolean;
  currency: string | null;
  contractor: {
    id: string | null;
    name: string | null;
    match: 'code' | 'name' | null;
    candidates: ResolvedCandidate[];
    from_document: { name: string | null; edrpou: string | null; ipn: string | null;
      iban: string | null; address: string | null };
  };
  buyer_check: { ok: boolean | null; document: { name: string | null; edrpou: string | null; ipn: string | null };
    organization: { name: string | null; codes: string[] } };
  warehouse_id: string | null;
  tax_invoice: ResolvedTaxInvoice;
  /** ч.1097: як читали файл — одна спроба чи з перечитуванням точнішим розпізнаванням. */
  ai?: {
    escalated: boolean;
    chosen: 'fast' | 'strong';
    reasons: string[];
    note: string | null;
    attempts: { tier: 'fast' | 'strong' | null; credits: string }[];
  };
  lines: ResolvedLine[];
  totals: { amount_without_vat: string; vat_amount: string; total_amount: string; document_total: string | null };
  checks: ResolvedCheck[];
  duplicate: { document_type: string | null; document_id: string | null; document_number: string | null } | null;
  notes: string | null;
}

export interface IncomingDocument extends IncomingDocumentListItem {
  attachment_id: string | null;
  source_text: string | null;
  recognized: Record<string, unknown> | null;
  resolved: Resolved | null;
  confirmed_at: string | null;
  /** ч.1096: для файлу зі станом SPLIT — документи, на які його розділено. */
  children: ChildRef[];
}

export interface ConfirmLine {
  line_no: number;
  nomenclature_id: string | null;
  description?: string | null;
  is_service: boolean;
  quantity: string;
  price: string;
  vat_rate_id: string;
  uom_id?: string | null;
  supplier_name: string;
  supplier_article?: string | null;
  suggested_nomenclature_id?: string | null;
  remember?: boolean;
}

export interface ConfirmPayload {
  target: Target;
  contractor_id: string;
  contract_id?: string | null;
  warehouse_id?: string | null;
  document_date: string;
  supplier_doc_number?: string | null;
  prices_include_vat: boolean;
  payment_due_date?: string | null;
  comment?: string | null;
  /** ч.1096: вхідна ПН до створюваної прибуткової накладної. */
  tax_invoice_id?: string | null;
  lines: ConfirmLine[];
}

export interface ConfirmResult {
  document_type: string;
  document_id: string;
  document_number: string;
}

export const recognition = {
  upload: (organizationId: string, files: File[]) => {
    const fd = new FormData();
    fd.append('organization_id', organizationId);
    for (const f of files) fd.append('files', f, f.name);
    // X-Silent-422: сторінка показує помилку сама (назва файлу + причина).
    return api.post<IncomingDocument[]>('/recognition/documents', fd,
      { headers: { 'X-Silent-422': '1' } }).then((r) => r.data);
  },
  list: (params: { organization_id?: string; status?: 'open' | IncomingStatus; limit?: number; offset?: number }) =>
    api.get<{ items: IncomingDocumentListItem[]; total: number }>('/recognition/documents', { params })
      .then((r) => r.data),
  get: (id: string) => api.get<IncomingDocument>(`/recognition/documents/${id}`).then((r) => r.data),
  /** Файл для перегляду (PDF/фото) — Blob через axios (JWT у заголовку). */
  fileBlob: async (id: string): Promise<Blob> => {
    const r = await api.get(`/recognition/documents/${id}/file`, { responseType: 'blob' });
    return r.data as Blob;
  },
  retry: (id: string) =>
    api.post<IncomingDocument>(`/recognition/documents/${id}/retry`).then((r) => r.data),
  confirm: (id: string, payload: ConfirmPayload) =>
    api.post<ConfirmResult>(`/recognition/documents/${id}/confirm`, payload,
      { headers: { 'X-Silent-422': '1' } }).then((r) => r.data),
  reject: (id: string, reason?: string | null) =>
    api.post<IncomingDocument>(`/recognition/documents/${id}/reject`, { reason: reason ?? null })
      .then((r) => r.data),
  remove: (id: string) => api.delete(`/recognition/documents/${id}`).then(() => undefined),
};

export default recognition;

export const STATUS_LABELS: Record<IncomingStatus, string> = {
  QUEUED: 'у черзі',
  RECOGNIZING: 'розпізнається',
  RECOGNIZED: 'розпізнано — перевірте',
  CONFIRMED: 'створено документ',
  REJECTED: 'відхилено',
  FAILED: 'помилка',
  SPLIT: 'розділено на документи',
};

export const KIND_LABELS: Record<DocKind, string> = {
  INVOICE: 'рахунок',
  DELIVERY_NOTE: 'видаткова накладна',
  ACT: 'акт',
  OTHER: 'інше',
};
